import { Search } from "lucide-react";
import React from "react";
import { useDispatch } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { Drawer } from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  AnalyticFiltersConfigType,
  DashboardPageName,
  SitewideAnalyticsPageName,
} from "@src/api/types/dashboards";
import { AnalyticsAccordion } from "@src/features/analytics/analyticsFilters/AnalyticsAccordion";
import { closeDrawer } from "@src/store/slices/drawers";

interface AnalyticsFiltersDrawerProps {
  isSaveLoading?: boolean;
  isSaveDisabled?: { isDisabled: boolean; reason: string };
  onFilterSave: () => void;
  openFilters: boolean;
  onFiltersClearAll: () => void;
  filterButtons: AnalyticFiltersConfigType[];
  pageName?: DashboardPageName | SitewideAnalyticsPageName;
}
export const AnalyticsFiltersDrawer = ({
  isSaveDisabled,
  onFilterSave,
  openFilters,
  isSaveLoading,
  onFiltersClearAll,
  pageName,
  filterButtons,
}: AnalyticsFiltersDrawerProps) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useImmer<string>("");
  return (
    <Drawer
      anchor="right"
      open={openFilters}
      onClose={() => {
        dispatch(closeDrawer("analyticsFilters"));
      }}
      sx={{
        // Portal order keeps the header below this drawer and its menus above it.
        zIndex: (theme) => theme.zIndex.modal,
        ".MuiDrawer-paper": { borderRadius: "0 !important", width: "500px" },
      }}
    >
      <Grid
        direction="column"
        rowSpacing={2}
        container
        sx={{
          height: "100%",
          display: "flex",
          flexWrap: "nowrap",
          padding: "20px",
        }}
      >
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid>
            <IgTypography fontWeight={600} variant={"h2"}>
              Filters
            </IgTypography>
          </Grid>
          <Grid>
            <IgButton color={"secondary"} onClick={onFiltersClearAll}>
              Clear All
            </IgButton>
          </Grid>
        </Grid>
        <Grid marginTop={"15px"}>
          <IgTextField
            placeholder={"Search"}
            slotProps={{
              input: {
                endAdornment: <Search size={16} />,
              },
            }}
            fullWidth
            value={search ?? ""}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>
        <Grid sx={{ overflow: "auto", flex: 1 }}>
          <AnalyticsAccordion
            filterButtons={filterButtons}
            pageName={pageName}
            search={search}
          />
        </Grid>
        <Grid>
          <IgTooltip title={isSaveDisabled?.reason || ""}>
            <Grid
              container
              direction={"row"}
              justifyContent={"flex-end"}
              paddingY={"20px"}
            >
              <IgButton
                sx={{ width: "100%" }}
                disabled={isSaveDisabled?.isDisabled}
                onClick={onFilterSave}
                loading={isSaveLoading}
              >
                Save Filters
              </IgButton>
            </Grid>
          </IgTooltip>
        </Grid>
      </Grid>
    </Drawer>
  );
};
